import axios, { AxiosInstance, AxiosResponse } from 'axios';
import cacheService from './cacheService';

class ApiService {
  private api: AxiosInstance;
  private requestQueue = new Map<string, Promise<any>>();
  private lastRequestTime = new Map<string, number>();
  private readonly THROTTLE_DELAY = 1000; // 1 second between identical requests

  constructor() {
    this.api = axios.create({
      baseURL: '/api',
      timeout: 45000, // 45 seconds timeout for Google Sheets operations
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Добавляем интерцептор для обработки ошибок
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Throttled GET request with caching
   */
  async get<T>(url: string, params?: Record<string, any>, useCache: boolean = true): Promise<T> {
    const cacheKey = cacheService.generateKey(url, params);
    
    // Check cache first
    if (useCache) {
      const cached = cacheService.get<T>(cacheKey);
      if (cached) {
        return cached;
      }
      
      // Skip request if we recently got empty results
      if (cacheService.shouldSkipEmptyRequest(cacheKey)) {
        console.log(`Skipping request to ${url} - recent empty response`);
        return [] as T;
      }
    }
    
    // Check if request is already in progress
    if (this.requestQueue.has(cacheKey)) {
      return this.requestQueue.get(cacheKey)!;
    }
    
    // Throttle identical requests
    const lastRequest = this.lastRequestTime.get(cacheKey);
    const now = Date.now();
    if (lastRequest && now - lastRequest < this.THROTTLE_DELAY) {
      console.log(`Throttling request to ${url}`);
      return new Promise((resolve) => {
        setTimeout(() => {
          this.get<T>(url, params, useCache).then(resolve);
        }, this.THROTTLE_DELAY - (now - lastRequest));
      });
    }
    
    this.lastRequestTime.set(cacheKey, now);
    
    // Make the request
    const requestPromise = this.makeRequest<T>(() => {
      const fullUrl = params ? `${url}?${new URLSearchParams(params).toString()}` : url;
      return this.api.get<T>(fullUrl);
    });
    
    this.requestQueue.set(cacheKey, requestPromise);
    
    try {
      const data = await requestPromise;
      
      // Cache successful responses
      if (useCache) {
        cacheService.set(cacheKey, data);
      }
      
      return data;
    } finally {
      this.requestQueue.delete(cacheKey);
    }
  }

  async post<T>(url: string, data?: any): Promise<T> {
    return this.makeRequest<T>(() => this.api.post(url, data));
  }

  async put<T>(url: string, data?: any): Promise<T> {
    return this.makeRequest<T>(() => this.api.put(url, data));
  }

  async delete<T>(url: string): Promise<T> {
    return this.makeRequest<T>(() => this.api.delete(url));
  }

  /**
   * Make HTTP request with error handling
   */
  private async makeRequest<T>(requestFn: () => Promise<AxiosResponse<T>>): Promise<T> {
    try {
      const response: AxiosResponse<T> = await requestFn();
      return response.data;
    } catch (error: any) {
      // Clear cache on error to allow retry
      if (error?.response?.status >= 500) {
        cacheService.clearAll();
      }
      throw error;
    }
  }
}

export default new ApiService();
